"use strict";
// Feature: address-geolocation, Task 5.1
//
// planGeocodeWrite — pure planner for the "explicit geocode this record"
// backend workflow.
//
// A resource Lambda (buildings / contacts / units) composes this planner
// with a `GeocodingProvider` and a load-write pair against DynamoDB. The
// planner itself is I/O-free: it takes a resolved owner, the current
// stored state, and a provider result, and returns the next `GeoLocation`
// plus the HTTP status the Lambda should return.
//
// Guarantees:
//   - Missing `ownerId` → 403 with `next = current` (Req 10.2).
//   - Cross-owner reference → 404 with `next = current` (Req 10.4).
//   - Provider throws / returns non-finite → 502 with `next = current`
//     (Req 6.4).
//   - Provider returns `null` → 422 with `next = current` (Req 3.5, 6.4).
//   - Invalid coordinates from provider → 400 with `next = current`
//     (Req 3.3).
//   - Success → 200 with `next` at the resolved coords, `source: 'geocoded'`,
//     `updatedAt: now`, and optional regenerated identifiers.
//
// Address fields are NEVER mutated by this planner (Req 6.3) — the caller
// is responsible for the record shape; this planner returns only the new
// `GeoLocation` + identifiers.
Object.defineProperty(exports, "__esModule", { value: true });
exports.planGeocodeWrite = void 0;
const models_1 = require("../models");
/**
 * Execute the geocoding workflow. This function is async because it calls
 * the provider; everything else is pure.
 */
async function planGeocodeWrite(input) {
    const { address, resolvedOwnerId, recordOwnerId, current, provider, now, generators, } = input;
    if (typeof resolvedOwnerId !== "string" || resolvedOwnerId.length === 0) {
        return { status: 403, next: current, error: "unresolved-owner" };
    }
    if (typeof recordOwnerId !== "string" ||
        recordOwnerId.length === 0 ||
        recordOwnerId !== resolvedOwnerId) {
        return { status: 404, next: current, error: "cross-owner" };
    }
    let providerResult;
    try {
        providerResult = await provider.geocode(address);
    }
    catch {
        return { status: 502, next: current, error: "provider-error" };
    }
    if (providerResult === null || providerResult === undefined) {
        return { status: 422, next: current, error: "unresolvable-address" };
    }
    const validation = (0, models_1.validateCoordinate)(providerResult.latitude, providerResult.longitude);
    if (!validation.ok) {
        return { status: 400, next: current, error: "invalid-coordinate" };
    }
    const next = {
        latitude: validation.latitude,
        longitude: validation.longitude,
        ...(typeof providerResult.accuracyMetres === "number"
            ? { accuracyMetres: providerResult.accuracyMetres }
            : {}),
        source: "geocoded",
        validated: true,
        updatedAt: now,
    };
    const identifiers = (0, models_1.regenerateIdentifiers)(validation.latitude, validation.longitude, generators);
    return { status: 200, next, identifiers };
}
exports.planGeocodeWrite = planGeocodeWrite;
